import {
  Button,
  TextInput,
  Text,
  Loader,
  Stack,
  Center,
  Box,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { displayErrorMessage } from "../shared/ErrorMessage";
import { initialize, updateField } from "../slices/form/register";
import { useEffect } from "react";
import {
  CreateUserRequest,
  useCreateUserMutation,
} from "../slices/api/usersApi";
import { useLocation } from "wouter";
import { isValidEmail } from "../shared/utility";
import { Logo } from "../shared/components/Logo";
import { useAuth } from "../authentication/AuthProvider";
import { getRecaptchaToken } from "../shared/recaptcha";

interface Props {
  email?: string;
}
const Register = (props: Props) => {
  const { data } = useAppSelector((state: RootState) => state.registerForm);
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [_, navigate] = useLocation();
  const { signOut } = useAuth();

  const dispatch = useAppDispatch();
  useEffect(() => {
    // Helps if user got signed in via a provider like Google, but didn't register
    signOut();
    // Preserving previously entered values
    dispatch(
      initialize({
        email: props.email,
        firstName: data?.firstName ?? "",
        lastName: data?.lastName ?? "",
      })
    );
  }, []);

  const handleClickRegister = async () => {
    if (data == null) {
      console.error("Data is undefined but should not be");
      return;
    }
    if (data.firstName == null) {
      displayErrorMessage("First name is required.");
      return;
    }
    if (data.email == null) {
      displayErrorMessage("Email is required.");
      return;
    }
    if (!isValidEmail(data.email)) {
      displayErrorMessage("Email is not valid.");
      return;
    }
    let params = "";
    const intendedURL = localStorage.getItem("intendedUrl");
    if (intendedURL) {
      params = "?redirect=" + encodeURIComponent(intendedURL);
    }

    const token = await getRecaptchaToken();

    try {
      const createUserRequest: CreateUserRequest = {
        email: data.email,
        firstName: data.firstName!,
        lastName: data.lastName,
        url: window.location.origin + "/emailLoginCompletion" + params,
        recaptchaToken: token,
      };
      await createUser(createUserRequest);
      window.localStorage.setItem("emailForSignIn", data.email);
      navigate("/waitForLoginEmail");
    } catch (error) {
      displayErrorMessage("Failed to register");
      return;
    }
  };

  if (!data) {
    return <Loader color="blue" />;
  }

  return (
    <Center style={{ height: "100vh" }}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleClickRegister();
        }}
      >
        <Stack align="center" justify="center" gap="sm">
          <Box w={100}>
            <Logo />
          </Box>
          <Text size="lg">Register to use Early Bird RSVP</Text>
          <TextInput
            required
            mt="md"
            label="Email"
            type="email"
            autoComplete="email"
            id="email"
            name="email"
            style={{ width: "300px" }}
            value={data.email ?? ""}
            onChange={(v) => {
              dispatch(
                updateField({
                  field: "email",
                  value: v.currentTarget.value,
                })
              );
            }}
          />
          <TextInput
            required
            mt="md"
            label="First Name"
            style={{ width: "300px" }}
            value={data.firstName ?? ""}
            onChange={(v) => {
              dispatch(
                updateField({
                  field: "firstName",
                  value: v.currentTarget.value,
                })
              );
            }}
          />
          <TextInput
            mt="md"
            label="Last Name"
            style={{ width: "300px" }}
            value={data.lastName ?? ""}
            onChange={(v) => {
              dispatch(
                updateField({
                  field: "lastName",
                  value: v.currentTarget.value,
                })
              );
            }}
          />
          <Button
            mt="md"
            style={{ width: "250px" }}
            onClick={handleClickRegister}
            disabled={
              data.email == null || data.firstName == null || isCreating
            }
            rightSection={isCreating ? <Loader size={14} /> : null}
          >
            Register
          </Button>
        </Stack>
      </form>
    </Center>
  );
};

export default Register;
